import { api } from "../api.js";
import { hydrateCommitteeIdentity, pageHeading, shell, statusPanel } from "../components.js";
import { escapeHtml, errorMessage, setSelectedCommittee } from "../utils.js";

function formatBytes(value) {
  return new Intl.NumberFormat("it-IT").format(Number(value) || 0);
}

function sourceMarkup(label, source) {
  return `<div class="r2-source">
    <div class="r2-source-heading"><strong>${escapeHtml(label)}</strong><span>HTTP ${escapeHtml(source.status)}</span></div>
    <dl>
      <div><dt>Dimensione</dt><dd>${formatBytes(source.bytes)} byte</dd></div>
      <div><dt>Tempo</dt><dd>${escapeHtml(source.durationMs)} ms</dd></div>
      <div><dt>SHA-256</dt><dd><code>${escapeHtml(source.canonicalSha256 || source.rawSha256)}</code></dd></div>
      ${source.etag ? `<div><dt>ETag</dt><dd><code>${escapeHtml(source.etag)}</code></dd></div>` : ""}
      ${source.cacheStatus ? `<div><dt>Cache</dt><dd>${escapeHtml(source.cacheStatus)}</dd></div>` : ""}
    </dl>
    <a href="${escapeHtml(source.url)}" target="_blank" rel="noopener noreferrer">Apri il JSON ${escapeHtml(label.toLowerCase())}</a>
  </div>`;
}

function differencesMarkup(comparison) {
  if (!comparison.differences.length) {
    return '<p class="r2-no-differences">Nessuna differenza rilevata.</p>';
  }
  const rows = comparison.differences.map((difference) => `<tr>
    <td><code>${escapeHtml(difference.path)}</code><small>${escapeHtml(difference.type)}</small></td>
    <td><code>${escapeHtml(difference.real)}</code></td>
    <td><code>${escapeHtml(difference.r2)}</code></td>
  </tr>`).join("");
  return `<div class="r2-diff-wrap"><table class="r2-diff-table">
    <thead><tr><th>Percorso JSON</th><th>API reale</th><th>R2</th></tr></thead>
    <tbody>${rows}</tbody>
  </table></div>${comparison.truncated ? '<p class="r2-diff-note">Sono mostrate soltanto le prime 100 differenze.</p>' : ""}`;
}

function comparisonMarkup(comparison) {
  return `<section class="r2-comparison ${comparison.equal ? "is-equal" : "has-differences"}">
    <div class="r2-comparison-heading">
      <div><span class="r2-status-badge">${comparison.equal ? "COERENTE" : "DIFFERENZE"}</span><h2>${escapeHtml(comparison.name)}</h2></div>
      <strong>${comparison.differences.length} differenze</strong>
    </div>
    <div class="r2-sources">
      ${sourceMarkup("API reale", comparison.real)}
      ${sourceMarkup("R2", comparison.r2)}
    </div>
    ${differencesMarkup(comparison)}
  </section>`;
}

function setRobotsNoIndex() {
  let robots = document.querySelector('meta[name="robots"]');
  if (!robots) {
    robots = document.createElement("meta");
    robots.name = "robots";
    document.head.append(robots);
  }
  robots.dataset.r2Diagnostic = "true";
  robots.content = "noindex,nofollow";
}

export async function r2CompareView(params, signal) {
  const { group } = params;
  const standingsOnly = params.section === "tables";
  const pageTitle = standingsOnly ? "Verifica classifica R2" : "Verifica dati R2";
  const backUrl = standingsOnly ? `/gironi/${group}/classifica` : `/gironi/${group}`;
  let committee = "";
  document.title = `${pageTitle} girone ${group} | FIPAV Online`;
  setRobotsNoIndex();

  const renderShell = () => {
    document.querySelector("#app").innerHTML = shell(
      pageHeading(pageTitle, `Girone ${group}`, backUrl) +
      `<div class="container r2-diagnostic-intro">
        <p>${standingsOnly ? "Confronto diagnostico della classifica" : "Confronto diagnostico dei dati"} tra il JSON generato dall’API reale e ${standingsOnly ? "quello" : "gli oggetti"} pubblicato${standingsOnly ? "" : "i"} su Cloudflare R2.</p>
        <button class="button" type="button" data-r2-refresh>Aggiorna confronto</button>
      </div>
      <div id="r2-comparison-content">${statusPanel("Confronto in corso…")}</div>`,
      { committee },
    );
  };

  const load = async () => {
    const content = document.querySelector("#r2-comparison-content");
    if (content) content.innerHTML = statusPanel("Confronto in corso…");
    try {
      const result = await api.r2Compare(group, signal);
      committee = result.committee || "";
      if (committee) {
        setSelectedCommittee(committee);
        renderShell();
        hydrateCommitteeIdentity(api, committee, signal);
      }
      const target = document.querySelector("#r2-comparison-content");
      if (!target) return;
      const checkedAt = new Intl.DateTimeFormat("it-IT", {
        dateStyle: "short",
        timeStyle: "medium",
      }).format(new Date(result.checkedAt));
      const isEqual = standingsOnly ? result.comparisons.tables.equal : result.equal;
      target.innerHTML = `<div class="container r2-overall ${isEqual ? "is-equal" : "has-differences"}">
        <span class="r2-status-badge">${isEqual ? "DATI COERENTI" : "DATI NON COERENTI"}</span>
        <div><strong>Girone ${escapeHtml(result.group)}</strong><span>Comitato ${escapeHtml(result.committee)} · verifica ${escapeHtml(checkedAt)}</span></div>
      </div>
      <div class="container r2-comparisons">
        ${standingsOnly ? "" : comparisonMarkup(result.comparisons.calendar)}
        ${comparisonMarkup(result.comparisons.tables)}
      </div>`;
      document.querySelector("[data-r2-refresh]")?.addEventListener("click", load, { once: true });
    } catch (error) {
      if (error.name !== "AbortError") {
        const target = document.querySelector("#r2-comparison-content");
        if (target) target.innerHTML = statusPanel(errorMessage(error), "error");
      }
    }
  };

  renderShell();
  document.querySelector("[data-r2-refresh]")?.addEventListener("click", load, { once: true });
  await load();
}
